<template>
  <div class="input-group flex-nowrap contest-summary-view" :style="stylePosition">
    <span
        v-if="position && !isHandRaise"
        class="input-group-text bg-white p-0"
        style="display: grid; grid-template-areas: 'pos up' 'pos down'; grid-template-columns: 3rem auto;"
    >
      <input
          type="number"
          class="contest-position-input"
          style="grid-area: pos; place-self: stretch; text-align: center"
          :value="position"
          min="1"
          :max="selectedQty"
          @change="changePosition"
          @keydown.enter.prevent="changePosition"
      >
      <button type="button" class="btn btn-light btn-sm rounded-0" @click="moveUp" :disabled="isAtTop">
        <i class="fas fa-chevron-up"/>
      </button>
      <button type="button" class="btn btn-light btn-sm rounded-0" @click="moveDown" :disabled="isAtBottom">
        <i class="fas fa-chevron-down"/>
      </button>
    </span>
    <button
        type="button"
        style="min-width: 0"
        class="btn flex-grow-1 hstack justify-content-between"
        :class="{
          'btn-primary': isSelected,
          'btn-secondary': !isSelected,
          disabled: (disabled && !isSelected) || locked
        }"
        @click="handleContest"
      >
      <span class="text-truncate"
            :class="{'text-light': isSelected}"
            v-text="contest.label"
      />
      <span class="d-flex flex-column align-items-center flex-shrink-1" style="min-width: 0">
        <span class="badge" :class="{'text-bg-light': isSelected, 'text-bg-primary': !isSelected}">
          {{ $tc("js.backend.voting_round_modal.option_counter", contest.options_count) }}
        </span>
      </span>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType, Locale } from "@/types";
import {ContestSelectionListContest} from "@/components/backend/ContestSelectionList.vue";

export default defineComponent({
	name: "ContestSummaryView",
	props: {
		contest: {
			type: Object as PropType<ContestSelectionListContest>,
			required: true,
		},
		currentLocale: {
			type: String as PropType<Locale>,
			required: true,
		},
		isSelected: {
			type: Boolean,
			required: true,
		},
		editMode: {
			type: Boolean,
			required: true,
		},
		position: {
			type: Number,
			required: true,
		},
		selectedQty: {
			type: Number,
			required: true,
		},
		orderError: {
			type: Boolean,
			default: false,
		},
    disabled: {
      type: Boolean,
      default: false,
    },
    isHandRaise: {
      type: Boolean,
      default: false,
    },
    locked: {
      type: Boolean,
      default: false,
    },
	},
	data() {
		return {
			maxOrder: 0,
		}
	},
	computed: {
    stylePosition() {
      if (isNaN(this.position)) {
        return { "grid-column": this.isSelected ? 2 : 1 };
      } else {
        return { "grid-row": this.position, "grid-column": this.isSelected ? 2 : 1 };
      }
    },
    isAtTop() {
      return this.position === 1;
    },
    isAtBottom() {
      return this.position === this.selectedQty;
    },
	},
	methods: {
		handleContest() {
      if (!this.isSelected && this.disabled) return;
			if (!this.editMode) this.$emit("handleContest", this.contest.reference, this.position);
		},
    moveUp() {
      if (this.isAtTop || this.editMode) return;
      this.$emit("handleContestPosition", this.contest.reference, this.position - 1);
    },
    moveDown() {
      if (this.isAtBottom || this.editMode) return;
      this.$emit("handleContestPosition", this.contest.reference, this.position + 1);
    },
		changePosition(event: Event) {
			if (this.editMode) return;

      (event.target as HTMLInputElement).value = Math.min(Math.max(1, Number((event.target as HTMLInputElement).value)), this.selectedQty).toString();
      this.$emit("handleContestPosition", this.contest.reference, (event.target as HTMLInputElement).value);
		},
	},
	watch: {
    selectedQty: function() {
			this.maxOrder = this.selectedQty;
    },
  },
});
</script>

<style lang="css" scoped>
.contest-position-input {
  border: none;
  background: none;
  -moz-appearance: textfield;
}

.contest-position-input::-webkit-inner-spin-button,
.contest-position-input::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.contest-summary-view {
  opacity: 1.0;
  min-width: 100%;
}

.disabled {
  opacity: 0.7;
}
</style>
